import { getClassroomApiUrl } from "@/js/utils/getBackendUrl";
import * as clsApi from "@/js/api/classroomApi";
import { formatDateAsDDMMYYYY } from "@/utils/dateTimeUtils";
import { ScheduleRule } from "@/components/SchedulingModal/ScheduleTableRow";
import { NewScheduleRule, UpdatedScheduleRule } from "@/query/scheduleQuery";

export enum ScheduleDayOfWeek {
	Monday = "MONDAY",
	Tuesday = "TUESDAY",
	Wednesday = "WEDNESDAY",
	Thursday = "THURSDAY",
	Friday = "FRIDAY",
	Saturday = "SATURDAY",
	Sunday = "SUNDAY",
}

export type ScheduleRequest = {
	day: ScheduleDayOfWeek;
	/**
	 * The start time of the schedule.
	 * Expected format: HH:MM in 24hr format (e.g. 14:00)
	 */
	startTime: string;
	/**
	 * The end time of the schedule.
	 * Expected format: HH:MM in 24hr format (e.g. 14:00)
	 */
	endTime: string;
	/**
	 * The end date of the schedule.
	 * Expected format: DD/MM/YYYY
	 */
	endDate: string;
	paused: boolean;
};

export type ScheduleResponse = {
	id: number;
	classId: string;
	day: ScheduleDayOfWeek;
	/**
	 * The start time of the schedule.
	 * Expected format: HH:MM
	 */
	startTime: string;
	/**
	 * The end time of the schedule.
	 * Expected format: HH:MM
	 */
	endTime: string;
	/**
	 * The end date of the schedule.
	 * Expected format: DD/MM/YYYY
	 */
	endDate: string;
	paused: boolean;
	modifiedBy: string;
	/**
	 * The date and time the schedule was last modified.
	 * Expected format: DD/MM/YYYY
	 */
	modifiedDate: string;
};

export type GetSchedulesResponse = {
	schedules: ScheduleResponse[];
};

export type ScheduleErrorResponse = {
	error: string;
};

export async function getClassSchedules(
	teacherUsername: string,
	classroomId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetSchedulesResponse> {
	const response = await fetch(`${host}/v1/appliances/${authHeaders.Device}/classrooms/${classroomId}/schedule-rules`, {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			user: teacherUsername,
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
	});

	if (!response.ok) {
		const body = (await response.json()) as ScheduleErrorResponse;
		throw new Error(`Failed to get class schedules: ${body.error}`);
	}

	return response.json();
}

export async function createSchedule(
	teacherUsername: string,
	classroomId: string,
	scheduleRequest: ScheduleRequest,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<ScheduleResponse> {
	const response = await fetch(`${host}/v1/appliances/${authHeaders.Device}/classrooms/${classroomId}/schedule-rules`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			user: teacherUsername,
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
		body: JSON.stringify(scheduleRequest),
	});

	if (!response.ok) {
		const body = (await response.json()) as ScheduleErrorResponse;
		throw new Error(`Failed to create schedule: ${body.error}`);
	}

	return response.json();
}

export async function updateSchedule(
	teacherUsername: string,
	classroomId: string,
	ruleId: string,
	scheduleRequest: ScheduleRequest,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<ScheduleResponse> {
	const response = await fetch(`${host}/v1/appliances/${authHeaders.Device}/classrooms/${classroomId}/schedule-rules/${ruleId}`, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			user: teacherUsername,
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
		body: JSON.stringify(scheduleRequest),
	});

	if (!response.ok) {
		const body = (await response.json()) as ScheduleErrorResponse;
		throw new Error(`Failed to update schedule: ${body.error}`);
	}

	return response.json();
}

export async function pauseSchedule(
	teacherUsername: string,
	classroomId: string,
	ruleId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<ScheduleResponse> {
	const response = await fetch(`${host}/v1/appliances/${authHeaders.Device}/classrooms/${classroomId}/schedule-rules/${ruleId}/pause`, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			user: teacherUsername,
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
	});

	if (!response.ok) {
		const body = (await response.json()) as ScheduleErrorResponse;
		throw new Error(`Failed to pause schedule: ${body.error}`);
	}

	return response.json();
}

export async function unpauseSchedule(
	teacherUsername: string,
	classroomId: string,
	ruleId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<ScheduleResponse> {
	const response = await fetch(`${host}/v1/appliances/${authHeaders.Device}/classrooms/${classroomId}/schedule-rules/${ruleId}/unpause`, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			user: teacherUsername,
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
	});

	if (!response.ok) {
		const body = (await response.json()) as ScheduleErrorResponse;
		throw new Error(`Failed to unpause schedule: ${body.error}`);
	}

	return response.json();
}

export async function deleteSchedule(
	teacherUsername: string,
	classroomId: string,
	ruleId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<void> {
	const response = await fetch(`${host}/v1/appliances/${authHeaders.Device}/classrooms/${classroomId}/schedule-rules/${ruleId}`, {
		method: "DELETE",
		headers: {
			"Content-Type": "application/json",
			user: teacherUsername,
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
	});

	if (!response.ok) {
		const body = (await response.json()) as ScheduleErrorResponse;
		throw new Error(`Failed to delete schedule: ${body.error}`);
	}
}

/**
 * Transforms a ScheduleResponse (API DTO) to a ScheduleRule (app model)
 */
export function transformScheduleResponseToRule(scheduleResponse: ScheduleResponse): ScheduleRule {
	if (!scheduleResponse) {
		throw new Error("Schedule response is undefined or null");
	}

	if (!scheduleResponse.startTime || !scheduleResponse.endTime) {
		throw new Error("Schedule response is missing required time fields");
	}

	// Parse time strings (format: "HH:MM") to TimeInput24Hr objects
	const startTimeParts = scheduleResponse.startTime.split(":");
	const endTimeParts = scheduleResponse.endTime.split(":");

	if (startTimeParts.length !== 2 || endTimeParts.length !== 2) {
		throw new Error("Schedule response has invalid time format (expected HH:MM)");
	}

	const [startHour, startMinute] = startTimeParts.map(Number);
	const [endHour, endMinute] = endTimeParts.map(Number);

	if (isNaN(startHour) || isNaN(startMinute) || isNaN(endHour) || isNaN(endMinute)) {
		throw new Error("Schedule response has invalid time values (not numbers)");
	}

	// Parse date string (format: "DD/MM/YYYY") to Date as local calendar date so it displays correctly in all timezones
	if (!scheduleResponse.endDate) {
		throw new Error("Schedule response is missing endDate field");
	}

	const dateParts = scheduleResponse.endDate.split("/");
	if (dateParts.length !== 3) {
		throw new Error("Schedule response has invalid date format (expected DD/MM/YYYY)");
	}

	const [day, month, year] = dateParts.map(Number);
	if (isNaN(day) || isNaN(month) || isNaN(year)) {
		throw new Error("Schedule response has invalid date values (not numbers)");
	}

	const endDate = new Date(year, month - 1, day); // local midnight (month is 0-indexed)

	return {
		id: scheduleResponse.id,
		day: scheduleResponse.day,
		startTime: {
			hour: startHour,
			minute: startMinute,
		},
		endTime: {
			hour: endHour,
			minute: endMinute,
		},
		endDate,
		active: !scheduleResponse.paused,
		modified: {
			by: scheduleResponse.modifiedBy,
			date: scheduleResponse.modifiedDate,
		},
	};
}

/**
 * Transforms a ScheduleRule (app model) to a ScheduleRequest (API DTO)
 */
export function transformScheduleRuleToRequest(scheduleRule: NewScheduleRule | UpdatedScheduleRule): ScheduleRequest {
	// Format time with leading zeros (e.g., "09:00" not "9:0")
	const formatTime = (hour: number, minute: number): string => {
		return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
	};

	return {
		day: scheduleRule.day,
		startTime: formatTime(scheduleRule.startTime.hour, scheduleRule.startTime.minute),
		endTime: formatTime(scheduleRule.endTime.hour, scheduleRule.endTime.minute),
		endDate: formatDateAsDDMMYYYY(scheduleRule.endDate),
		paused: !scheduleRule.active,
	};
}
